/**
 * Dashboard JS
 *
 * 대시보드 Page에서 사용하는 자바스크립트 모듈입니다.
 *
 * widgetMixin: 위젯 내부 컨텐츠에 High-charts 객체의 Resize 기능을 제공합니다.
 *
 * @since 2018-05-15
 * @author ms1124.kim
 */
// Moment 시간처리 모듈
import Moment from 'moment'

// 전역 이벤트버스 모듈
import Bus from '~/plugins/event-hub'

// 대시보드 공통 유틸
import {
  top5Color,
  top10Color,
  kmgtConverter,
  bpsConverter,
  ppsConverter,
  floorSecondTime,
  byteConverter,
  separateByComma,
  trafficTickPositioner
} from '~/utils/charts'
import {getGeoFillColorByCount} from '~/utils/dashboard'

import vlanSelectPopup from '~/pages/dashboard/dashboard/popup/vlanSelectPopup'

import {createNamespacedHelpers} from 'vuex'
// store helper
const multirwHelper = createNamespacedHelpers('common/multirw')

/*
  Object - widgetMixin
  Description - 대시보드 위젯에 공통으로 적용되는 Mixin 객체.
*/
const widgetMixin = {
  data () {
    return {
      apiCallFail: false,
      apiCallFailMsg: '',
      // TAMS
      renderPopUpComponent: void 0,
      wrapperWidth: 0,
      wrapperHeight: 0,
      isIpType: false,
      isDeviceFilter: true,
      isIpFilter: false,
      ip_exp_list: []
    }
  },
  components: {
    // TAMS
    vlanSelectPopup
    // 'device-dialog': FilterDeviceDialog
    // DeviceDialog
  },
  props: {
    i: {
      type: String,
      default: ''
    },
    uuid: {
      type: String,
      default: ''
    },
    parent: {
      type: Object
    },
    vlanOpt: {
      type: Number,
      default: void 0
    },
    deviceTypeOpt: {
      type: Array,
      default: []
    },
    ipTypeOpt: {
      type: String,
      default: ''
    },
    initOptions: {
      type: Object,
      default: () => {}
    },
    haMemberOpt: {
      type: String,
      default: ''
    },
    syncApiCall: {
      type: Boolean,
      default: false
    },
    syncApiCallMsg: {
      type: [String, Number],
      default: ''
    },
    syncWidgetData: {
      type: [Object, Array]
    },
    isMaximumWidget: {
      type: Boolean,
      default: false
    }
  },
  // TAMS
  computed: {
    top5Color: () => top5Color,
    top10Color: () => top10Color,
    ...multirwHelper.mapState([
      'thisMenuPermission'
    ]),
    /*
      이름: widgetBasicMenu
      설명: 대시보드 위젯의 공통 메뉴를 지정한다. 기본값은 위젯 삭제만 존재한다.
    */
    widgetBasicMenu () {
      let basicMenu = [] // TAMS

      // TAMS, 해당 메뉴 권한이 있을 경우만 (쓰기 권한)
      if (this.thisMenuPermission) {
        basicMenu = [
          {
            key: 'menu_delwidget',
            icon: 'q-icon q-chart-icon icon-chart-delete',
            label: this.$t('dash.dashboardDeleteWidget'),
            click: this.onClickRemoveWidget
          }
        ]
      }

      // 새로고침이 허용되면 메뉴 추가
      if (this.isRefresh !== false) {
        basicMenu.unshift({
          key: 'menu_refresh',
          icon: 'q-icon q-chart-icon icon-chart-refresh',
          label: this.$t('dash.dashboardRefreshWidget'),
          click: this.onClickRefreshWidget
        })
      }

      // 최대화가 허용되면 메뉴 추가
      if (this.isMaximum !== false) {
        basicMenu.unshift({
          key: 'menu_fullscreen',
          icon: 'q-icon q-chart-icon icon-chart-zoom-in',
          label: this.$t('dash.dashboardMaximumWidget'),
          click: this.onClickMaximumWidget
        })
      }

      return basicMenu
    },
    /**
     * 이름: getFailApiCallMsg
     * 설명: API 호출에 실패하면 원인에 따라 출력할 문구를 결정한다.
     */
    getFailApiCallMsg () {
      return this.apiCallFailMsg === '' ? '' : this.apiCallFailMsg === 404 ? this.$t('dash.widgetConnFail_msg_connection_fail') : this.$t('dash.widgetCallFail_msg_call_api_fail')
    },
    /**
     * 이름: getFailConnBtnStyle
     * 설명: 장비에 접속 실패 시 버튼 스타일을 리턴한다.
     */
    getFailConnBtnStyle () {
      return this.isMaximumWidget ? 'bs bs-q-btn-md marT10' : 'bs bs-q-btn-sm marT10'
    },
    parentOpts () {
      return this.$parent.opts || null
    }
  },
  created () {
    const defaultOpts = this.opts || {}

    this.renderPopUpComponent = void 0
    this.opts = {
      vlanId: this.initOptions.vlanId,
      unit: this.initOptions.unit,
      ipType: this.initOptions.ipType || '',
      haMember: this.initOptions.haMember || '',
      chartType: this.initOptions.chartType || defaultOpts.chartType,
      // TAMS
      sortBy: this.initOptions.sortBy || defaultOpts.sortBy,
      mach_id: this.initOptions.mach_id || defaultOpts.mach_id,
      mach_id_options: this.initOptions.mach_id_options || defaultOpts.mach_id_options,
      visibleKeys: this.initOptions.visibleKeys || defaultOpts.visibleKeys,
      deviceType: this.initOptions.deviceType || [],
      // 위젯 파일에 설정된 isDeviceFilter 값이 있을 경우 우선 적용(defaultOpts).
      isDeviceFilter: this.initOptions.isDeviceFilter || _.isNil(defaultOpts.isDeviceFilter) ? true : defaultOpts.isDeviceFilter,
      ip_exp_list: this.initOptions.ip_exp_list
    }

    // 위젯 본체에 차트 타입 추가
    if (this.parent && this.widgetChartMenu) {
      this.parent.setChartType(this.opts.chartType)
    }
    if (this.parent && this.opts.vlanId) {
      this.parent.setSelectVlanId(this.opts.vlanId)
    }
    // 장비 필터는 기본 옵션으로 사용 안하는 위젯인 경우 해당 위젯 파일에 isDeviceFilter: false로 설정 필요.
    // -. 사용 안하는 위젯일 경우  isDeviceFilter: false 로 설정.
    // -. 사용 하는 위젯일 경우    true 설정 되게 함.
    this.isDeviceFilter = this.opts.isDeviceFilter
    if (this.parent && this.isDeviceFilter) {
      this.parent.setSelectMachId(this.opts.mach_id || [])
    }
    this._onResizeContentsFromWidget = _.throttle(this.onResizeContentsFromWidget, 250)

    window.addEventListener('resize', this._onResizeContentsFromWidget)
  },
  async mounted () {
    // 위젯의 name 속성 가져오기
    const widgetName = this.$options.name

    // preExecTask를 DOM에 추가 (데이터 테이블이 위젯에서 제대로 Destroy 되지 않는 문제)
    this.$parent.$el.preExecTask = this.preExecTask

    // 위젯 메뉴 설정 : plugin/gridLayout/GridWidget.vue > setWidgetMenu 호출 - 위젯 최대화에서는 해당 내용 생략
    if (!this.isMaximumWidget) {
      try {
        const widgetType = {
          haType: this.haType,
          ipType: this.isIpType,
          isVlan: this.isVlan,
          deviceType: this.deviceType,
          isDeviceFilter: this.isDeviceFilter,
          isIpFilter: this.isIpFilter
        }
        await this.parent.setWidgetHeaderOptions(widgetType, this.widgetBasicMenu, this.widgetChartMenu, this.widgetCustomMenu)
      } catch (e) {
        this.$log('[Dashboard GUI Error]', e)
      }
    }

    // 확장형 위젯인 경우 (위젯 자체에 요소를 펼치는 기능이 있는 경우 - IPSec VPN 터널 상태, HA 상태, 서비스 연동 상태 3개만 해당)
    if (this.expandableWidget) {
      Bus.addEventBus(`onFoldWidget_${widgetName}`, this.onFoldWidgetContents)
    }

    // 이벤트 버스를 사용하고, isSync를 사용하지 않는경우 (isSync : 원본 위젯의 widgetData를 동기화 할 때 사용 - 데이터 테이블 전용)
    if (this.eventBus && !this.isSync) {
      Bus.addEventBus(this.isMaximumWidget ? `${widgetName}_maximum` : widgetName, this.getWidgetContentsData)
    }

    // 이벤트 버스 지정 - 모든위젯 강제 새로고침
    Bus.addEventBus(`${widgetName}_force_refresh`, this.onClickRefreshWidget)

    // 중복 위젯 등록 기능 추가되면서 Queue에 위젯 이름이 중복 등록 되고있어 중복 등록 된 위젯 Event 이름 Filter 코드 추가.
    Bus.eventBusQueue = _.uniq(Bus.eventBusQueue)

    // 위젯이 최대화 상태이고, isSync를 사용하는 경우 -> 초기 데이터를 API로 조회하는 것이 아닌 전달된 데이터로 초기화
    if (this.isMaximumWidget && this.isSync) {
      if (this.syncWidgetData.length > 0) {
        this.widgetData = _.cloneDeep(this.syncWidgetData)
      }

      this.apiCallFail = this.syncApiCall
      this.apiCallFailMsg = this.syncApiCallMsg
    } else {
      this.getWidgetContentsData(true)
    }

    // 최초 마운트 시 위젯 사이즈 지정 (차트 사이즈 설정)
    this.$nextTick(function () {
      this.$parent.$el.widget = this
      this.onResizeContentsFromWidget()
    })
  },
  beforeDestroy () {
    const widgetName = this.$options.name

    // 확장형 위젯인 경우 (위젯 자체에 요소를 펼치는 기능이 있는 경우 - IPSec VPN 터널 상태, HA 상태, 서비스 연동 상태 3개만 해당)
    if (this.expandableWidget) {
      Bus.removeEventBus(`onFoldWidget_${widgetName}`)
    }

    // 이벤트 버스를 사용하고, isSync를 사용하지 않는 경우
    if (this.eventBus && !this.isSync) {
      // 이벤트 버스 제거.
      Bus.removeEventBus(this.isMaximumWidget ? `${widgetName}_maximum` : widgetName)
    }

    // 이벤트 버스 제거 - 모든위젯 강제 새로고침 이벤트버스 제거
    Bus.removeEventBus(`${widgetName}_force_refresh`)

    // 리사이즈 이벤트 제거
    window.removeEventListener('resize', this._onResizeContentsFromWidget)

    this._onResizeContentsFromWidget = null

    // 부모 속성 제거
    this.$parent.$el.widget = null
    this.$parent.$el.preExecTask = null

    this.apiCallFail = false
    this.apiCallFailMsg = ''
  },
  methods: {
    bpsConverter,
    ppsConverter,
    byteConverter,
    floorSecondTime,
    kmgtConverter, // TAMS
    separateByComma,
    // genUuid,
    // getWidgetByName,
    // getWidgetContent,
    getGeoFillColorByCount,
    trafficTickPositioner, // TAMS
    /**
      이름: onChangeMachId
      설명: 필터에 장비 List를 변경한다.
    */
    onChangeMachId (machId = []) {
      this.opts.mach_id = machId
    },
    /*
      이름: onChangeVlanId
      설명: 트래픽 추이를 출력할 VLAN을 선택한다.
    */
    onChangeVlanId (vlanId = void 0) {
      this.$emit('changeVlanId', vlanId)
    },
    /*
      이름: getMomentData
      설명: 전달된 시간 정보를 문자열로 리턴한다.
    */
    getMomentData (timeStamp, format = 'YYYY-MM-DD HH:mm:ss') {
      return Moment(timeStamp).format(format)
    },
    /*
      이름: setMomentData
      설명: 전달된 시간 정보를 설정한다.
    */
    setMomentData (timeValue, funcType, timeRange, timeType) {
      return Moment(timeValue)[funcType](timeRange, timeType)
    },
    /*
      이름: setRealtimeTimer
      설명: 실시간 위젯일 경우 타이머를 설치한다.
    */
    setRealtimeTimer () {
      // 설치되어있는 타이머 제거
      this.clearRealtimeTimer()

      // 새로운 타이머 설치
      this.timerObject = setInterval(this.getWidgetContentsData, this.timerValue)
    },
    /*
      이름: clearRealtimeTimer
      설명: 실시간 위젯의 타이머를 제거한다.
    */
    clearRealtimeTimer () {
      if (this.timerObject) {
        clearInterval(this.timerObject)
      }
    },
    /*
      이름: onChangePieChart, onChangeBarChart, onChangeColumnChart, onChangeTreeMapChart
      설명: TopN 타입의 위젯에서 차트 종류를 변경할 때 사용한다.
    */
    onChangePieChart () {
      this.opts.chartType = 'pie'
    },
    onChangeBarChart () {
      this.opts.chartType = 'bar'
    },
    onChangeColumnChart () {
      this.opts.chartType = 'column'
    },
    onChangeTreeMapChart () {
      this.opts.chartType = 'treemap'
    },
    onChangeSunburst () {
      this.opts.chartType = 'sunburst'
    },
    /*
      이름: onClickRemoveWidget
      설명: 위젯을 삭제한다.
    */
    onClickRemoveWidget (event, dropdown) {
      // 버전업으로 인스턴스가 삭제될 경우 v-close-overlay가 제대로 동작하지 않아 수동으로 hide 메서드를 호출하는 코드 추가
      if (dropdown) {
        dropdown.hide()
      }
      this.$emit('removeWidget')
    },
    /*
      이름: onClickMaximumWidget
      설명: 대상 위젯의 컨텐츠를 최대화처리된 팝업으로 출력한다.
    */
    onClickMaximumWidget () {
      this.$emit('maximumWidget')
    },
    /*
      이름: onClickRefreshWidget
      설명: 위젯의 데이터를 초기화 후 데이터를 새로 구성한다.
    */
    onClickRefreshWidget () {
      this.getWidgetContentsData(true)
    },
    /*
      이름: onShowVlanPopUp
      설명: 위젯의 VLAN 선택 창 호출한다.
    */
    onShowVlanPopUp () {
      this.toggleComponent('vlanSelectPopup')
    },
    /*
      이름: toggleComponent
      설명: target 팝업 창 호출한다.
    */
    toggleComponent (target) {
      this.renderPopUpComponent = ''
      this.$nextTick(() => {
        this.renderPopUpComponent = target
      })
    },
    /*
      이름: showProgress
      설명: API에서 데이터를 가져올 때 프로그래스를 출력 / 숨김 처리한다.
    */
    showProgress (show = false) {
      this.$emit('showProgress', show)
    },
    /**
     * 이름: onClickChangeHaLocal
     * 설명: 현재 위젯의 HA 멤버를 Local로 변경한다. 이 기능은 저장 버튼을 누르지 않으면 DB에 저장되지 않는다.
     */
    onClickChangeHaLocal () {
      // 내부 모델에 UUID 변경
      this.opts.haMember = this.uuid

      // 최대화, 일반 위젯에서 상단 HA Member SelectBox 값 변경
      if (this.uuid) {
        this.parent.setDeviceToUuid(this.uuid)
      }

      // 최대화 위젯의 경우 값이 변경되면 자동으로 원본 위젯을 재조회하므로 필요 없음
      if (!this.isMaximumWidget) {
        this.getWidgetContentsData(true)
      }
    },
    /*
      이름: onFoldWidgetContents
      설명: 대상 위젯이 확장되어 있을 경우, 닫기로 변경한다.
    */
    onFoldWidgetContents (targetWidget) {
      if (this.$options.name !== targetWidget) {
        this.isExpand = false
      }
    },
    /*
      이름: onClickExpandHaList
      설명: 확장 버튼을 클릭하면 스타일을 변경하고, 다른 확장형 위젯에게 리스트 닫기를 요청한다.
    */
    onExpandWidgetContents () {
      this.isExpand = !this.isExpand

      // 위젯이 확장된 상태라면, 나머지 확장형 위젯에게 닫기 요청
      if (this.isExpand) {
        for (const eventBus of Bus.eventBusQueue.filter(eventName => eventName.indexOf('onFoldWidget_') > -1)) {
          Bus.$emit(eventBus, this.$options.name)
        }
      }
    },
    /*
      이름: onResizeContentsFromWidget
      설명: 위젯 내부에 있는 컨텐츠의 사이즈를 다시 조절한다.
    */
    onResizeContentsFromWidget () {
      // Chart 래퍼 컨테이너 객체
      const chartWrapper = this.$refs.chartWrapper

      // 래퍼 체크 (임시 코드)
      if (chartWrapper) {
        // 다른 컨텐츠 영역이 있는지 확인
        // const otherContents = this.$refs.otherContents

        // 옵션 영역이 있는지 확인
        const chartOption = this.$refs.chartWrapperOpts
        const chartInstance = _.has(this.$refs.chartInstance, 'getChartInstance') ? this.$refs.chartInstance.getChartInstance() : this.$refs.chartInstance

        // Chart 인스턴스
        if (chartInstance) {
          let wrapperWidget = chartWrapper.clientWidth
          let wrapperHeight = chartWrapper.clientHeight < 249 ? 249 : chartWrapper.clientHeight || 249

          if (chartWrapper.className.indexOf('pad0') < 0) {
            wrapperWidget -= 24
            wrapperHeight -= 24
          }

          if (this.isMaximumWidget) {
            wrapperHeight = this.parent.$el.clientHeight - 70
          }

          // Chart 사이즈 조절
          chartInstance.setSize(wrapperWidget, wrapperHeight - ((chartOption && this.applyOptionHeight) ? chartOption.clientHeight : 0), false)
        }
      }
    }
  },
  watch: {
    deviceTypeOpt (type) {
      this.opts.deviceType = type
    },
    vlanOpt (newVlan) {
      this.opts.vlanId = newVlan
    },
    ipTypeOpt (newIpType) {
      this.opts.ipType = newIpType
    },
    haMemberOpt (newHaMember) {
      this.opts.haMember = newHaMember
    }
  }
}

export {
  widgetMixin
}
